import { QueryClient, QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import * as dayjs from 'dayjs';

import API from '@api/API';
import { ToastNotification } from '@api/handleResponse';

import { useAnalytics } from '@hooks/useAnalytics';
import { sessionId } from '@hooks/useReferredBy';

import { useAppContext } from '../contexts/AppContext';
import { AppStorage, AppStorageKey, useAppStorageValue } from '../utilities/appStorage';
import { FeedEvent } from './EventQueries';
import { Promoter } from './PromoterQueries';

interface OnboardingProperties {
  isComplete: boolean;
  basicInfoIsComplete?: boolean;
  interestsAreComplete?: boolean;
  discoverIsComplete?: boolean;
  communityIsComplete?: boolean;
}

export interface CurrentUser {
  _id: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  isSuperAdmin?: boolean;
  isDragKingCommunityAdmin?: boolean;
  notificationsEnabled?: boolean;
  smsNotificationsEnabled?: boolean;
  onboarding?: OnboardingProperties;
  entertainerId?: string;
  isAgeGateVisible?: boolean;
  isOver21?: boolean;
}

export interface SplashNotification {
  isRead?: boolean;
  _id: string;
  imageUrl?: string;
  title: string;
  content: string;
  displayPath: string;
  button?: {
    text: string;
    to: string;
    action?: () => void;
  };
}

export interface CurrentUserResponseBody {
  token?: string;
  user: CurrentUser;
  splashNotifications: SplashNotification[];
}

export const useCurrentUser = () => {
  const { userIsLoggedIn } = useAppContext();

  const storedUser = useAppStorageValue(AppStorageKey.User);

  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentUser.queryKey(),
    queryFn: useCurrentUser.queryFn,
    enabled: userIsLoggedIn,
  });

  return {
    currentUserIsReady: isFetched,
    currentUser: data?.user ?? storedUser,
    refetchUser: refetch,
  };
};
useCurrentUser.queryKey = (): QueryKey => ['user'];
useCurrentUser.queryFn = async () => {
  const response = await API.get<CurrentUserResponseBody>(`/v1/user/me`);

  if (response?.token) {
    localStorage.setItem('token', response.token);
  }

  await AppStorage.setItem(AppStorageKey.User, response.user);

  return response;
};

export const useSplashNotifications = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentUser.queryKey(),
    queryFn: useCurrentUser.queryFn,
    enabled: userIsLoggedIn,
  });

  return {
    splashNotificationsAreReady: isFetched,
    splashNotifications: data?.splashNotifications,
    refetchSplashNotifications: refetch,
  };
};

export const useToastNotification = () => {
  const queryClient = useQueryClient();
  const { userIsLoggedIn } = useAppContext();

  const { data } = useQuery<ToastNotification>({
    queryKey: useToastNotification.queryKey(),
    queryFn: () => {
      return queryClient.getQueryData<ToastNotification>(useToastNotification.queryKey()) ?? null;
    },
    enabled: userIsLoggedIn,
  });

  return {
    toastNotificationIsReady: true,
    toastNotification: data,
  };
};
useToastNotification.queryKey = () => ['toast-notification'];
useToastNotification.queryFn = () => {};

export const fetchCurrentUser = async (queryClient: QueryClient) => {
  await queryClient.invalidateQueries({ queryKey: useCurrentUser.queryKey() });

  return queryClient.fetchQuery({
    queryKey: useCurrentUser.queryKey(),
    queryFn: useCurrentUser.queryFn,
  });
};

export const useFetchCurrentUser = () => {
  const queryClient = useQueryClient();

  return () => fetchCurrentUser(queryClient);
};

export const useIsValidTokenQuery = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useIsValidTokenQuery.queryKey(),
    queryFn: useIsValidTokenQuery.queryFn,
    enabled: userIsLoggedIn,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  if (!userIsLoggedIn) {
    return {
      tokenIsValidIsReady: true,
      tokenIsValid: false,
      refetchTokenIsValid: () => {},
    };
  }

  return {
    tokenIsValidIsReady: isFetched,
    tokenIsValid: data?.tokenIsValid,
    refetchTokenIsValid: refetch,
  };
};
useIsValidTokenQuery.queryKey = () => ['tokenIsValid'];
useIsValidTokenQuery.queryFn = () =>
  API.get<{ tokenIsValid: boolean }>(`/v1/token/validate?sessionId=${sessionId}`);
export const fetchTokenIsValid = async (queryClient: QueryClient) => {
  await queryClient.invalidateQueries({ queryKey: useIsValidTokenQuery.queryKey() });

  return queryClient.fetchQuery({
    queryKey: useIsValidTokenQuery.queryKey(),
    queryFn: useIsValidTokenQuery.queryFn,
  });
};

export const useFetchTokenIsValid = () => {
  const queryClient = useQueryClient();

  return () => fetchTokenIsValid(queryClient);
};

const NOTIFICATION_TYPES = ['suggestedEvents'] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export interface Notification {
  _id: string;
  text: string;
  sentAt: string;
  seenAt?: string;
  readAt?: string;
  type: NotificationType;
  to: string;
}

export const useNotificationsQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useNotificationsQuery.queryKey(),
    queryFn: () => useNotificationsQuery.queryFn(),
  });

  return {
    notificationsAreReady: isFetched,
    notifications: data?.notifications,
    refetchNotifications: refetch,
  };
};
useNotificationsQuery.queryKey = () => ['notifications'];
useNotificationsQuery.queryFn = () =>
  API.get<{ notifications: Notification[] }>(`/v1/user/notifications`);

export const useFollowsQuery = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useFollowsQuery.queryKey(),
    queryFn: useFollowsQuery.queryFn,
    enabled: userIsLoggedIn,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    followsAreReady: isFetched,
    follows: data?.follows,
    refetchFollows: refetch,
  };
};
useFollowsQuery.queryKey = () => ['user-follows'];
useFollowsQuery.queryFn = () =>
  API.get<{ follows: { [key: string]: { [documentId: string]: true } } }>(`/v1/user/follows`);

export const useUserInterestsQuery = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserInterestsQuery.queryKey(),
    queryFn: () => useUserInterestsQuery.queryFn(),
    enabled: userIsLoggedIn,
  });

  return {
    userInterestsAreReady: isFetched,
    userInterests: data?.tags,
    refetchUserInterests: refetch,
  };
};
useUserInterestsQuery.queryKey = () => ['user', 'interests'];
useUserInterestsQuery.queryFn = () =>
  API.get<{ tags: { _id: string; title: string }[] }>(`/v1/user/interests`);

export const useInterestsQuery = ({ searchValue }: { searchValue: string }) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useInterestsQuery.queryKey(searchValue),
    queryFn: () => useInterestsQuery.queryFn(searchValue),
    enabled: !!searchValue,
  });

  const { track } = useAnalytics();
  useEffect(() => {
    if (searchValue) {
      track('Interests Search', { Search: searchValue });
    }
  }, [searchValue]);

  return {
    interestsAreReady: isFetched,
    interests: data?.tags,
    refetchInterests: refetch,
  };
};
useInterestsQuery.queryKey = (searchValue: string) => ['interests', searchValue];
useInterestsQuery.queryFn = (searchValue: string) =>
  API.get<{ tags: { _id: string; title: string }[] }>(`/v1/interests?s=${searchValue}`);

export const useInterestQuery = (tagId: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useInterestQuery.queryKey(tagId),
    queryFn: () => useInterestQuery.queryFn(tagId),
    enabled: !!tagId,
  });

  return {
    interestIsReady: isFetched,
    interest: data,
    refetchInterest: refetch,
  };
};
useInterestQuery.queryKey = (tagId: string) => ['interests', 'results', tagId];
useInterestQuery.queryFn = (tagId: string) =>
  API.get<{ tag: { _id: string; title: string }; events: any[]; venues: any[] }>(
    `/v1/user/interests/${tagId}`,
  );

export const usePopularInterestsQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: usePopularInterestsQuery.queryKey(),
    queryFn: () => usePopularInterestsQuery.queryFn(),
  });

  return {
    popularInterestsAreReady: isFetched,
    popularInterests: data?.tags,
    refetchInterests: refetch,
  };
};
usePopularInterestsQuery.queryKey = () => ['popular-interests', dayjs().format('YYYY-MM-DD')];
usePopularInterestsQuery.queryFn = () =>
  API.get<{ tags: { _id: string; title: string }[] }>(`/v1/interests/popular`);

interface SearchParams {
  cityId?: string;
  searchValue?: string;
  startDate?: string;
  endDate?: string;
}
export const useSearchQuery = (searchParams: SearchParams) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useSearchQuery.queryKey(searchParams),
    queryFn: () => useSearchQuery.queryFn(searchParams),
    enabled: Boolean(searchParams.searchValue || searchParams.startDate || searchParams.endDate),
  });

  return {
    searchResultsAreReady: isFetched,
    searchResults: data,
    refetchSearchResults: refetch,
  };
};
useSearchQuery.queryKey = ({ searchValue, startDate, endDate, cityId }: SearchParams) => [
  'search',
  'results',
  searchValue,
  startDate,
  endDate,
  cityId,
];
useSearchQuery.queryFn = ({ searchValue, startDate, endDate, cityId }: SearchParams) =>
  API.post<{ events: FeedEvent[]; venues: any[]; promoters: Promoter[] }, SearchParams>(
    `/v1/events/search`,
    {
      searchValue,
      startDate,
      endDate,
      cityId,
    },
  );

interface UserConversation {
  entertainerId?: string;
  userId?: string;
  promoterId?: string;
  isUnread: boolean;
  lastMessageAt: string;
  text?: string;
  title: string;
  _id: string;
}

export const useUserConversationsQuery = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserConversationsQuery.queryKey(),
    queryFn: () => useUserConversationsQuery.queryFn(),
    enabled: userIsLoggedIn,
  });

  return {
    userConversationsAreReady: isFetched,
    userConversations: data?.conversations ?? { user: [], promoter: [], entertainer: [] },
    refetchUserConversations: refetch,
  };
};

useUserConversationsQuery.queryKey = (userId?: string) => ['conversations', userId];
useUserConversationsQuery.queryFn = () =>
  API.get<{
    conversations: {
      user: UserConversation[];
      promoter: UserConversation[];
      entertainer: UserConversation[];
    };
  }>('/v1/conversations/me');

export const useManageCompetitionsQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useManageCompetitionsQuery.queryKey(),
    queryFn: useManageCompetitionsQuery.queryFn,
  });

  return {
    competitionsAreReady: isFetched,
    competitions: data?.campaigns,
    refetchCompetitions: refetch,
  };
};

useManageCompetitionsQuery.queryKey = () => ['competitions'];
useManageCompetitionsQuery.queryFn = () =>
  API.get<{ campaigns: Record<string, any[]> }>(`/v1/user/promoters/campaigns`);
export const fetchManageCompetitions = async (queryClient: QueryClient) => {
  await queryClient.invalidateQueries({ queryKey: useManageCompetitionsQuery.queryKey() });

  return queryClient.fetchQuery({
    queryKey: useManageCompetitionsQuery.queryKey(),
    queryFn: useManageCompetitionsQuery.queryFn,
  });
};

export const useUserChallengesQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserChallengesQuery.queryKey(),
    queryFn: useUserChallengesQuery.queryFn,
  });

  return {
    challengesAreReady: isFetched,
    challenges: data?.userChallenges,
    refetchChallenges: refetch,
  };
};

useUserChallengesQuery.queryKey = () => ['challenges'];
useUserChallengesQuery.queryFn = () => API.get<{ userChallenges: any[] }>(`/v1/user/challenges`);

export const useUserWalletQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserWalletQuery.queryKey(),
    queryFn: () => useUserWalletQuery.queryFn(),
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    walletIsReady: isFetched,
    wallet: data,
    refetchWallet: refetch,
  };
};
useUserWalletQuery.queryKey = () => ['wallet'];
useUserWalletQuery.queryFn = () => API.get<{ total: number }>(`/v1/user/wallet`);

export const useUserTalentQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserTalentQuery.queryKey(),
    queryFn: () => useUserTalentQuery.queryFn(),
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    talentIsReady: isFetched,
    talent: data,
    refetchTalent: refetch,
  };
};
useUserTalentQuery.queryKey = () => ['talent'];
useUserTalentQuery.queryFn = () =>
  API.get<{ bookingOpportunities: any[]; bookingRequests: any[] }>(`/v1/user/talent`);

interface PublicDetailQuestion {
  _id: string;
  documentType: string;
  question: string;
  options: string[];
}

export const useDetailQuestionsQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useDetailQuestionsQuery.queryKey(),
    queryFn: () => useDetailQuestionsQuery.queryFn(),
  });

  return {
    detailQuestionsAreReady: isFetched,
    detailQuestionsByDocumentType: data?.detailQuestionsByDocumentType,
    refetchDetailQuestions: refetch,
  };
};

useDetailQuestionsQuery.queryKey = () => ['detail-questions'];
useDetailQuestionsQuery.queryFn = () =>
  API.get<{
    detailQuestionsByDocumentType: {
      booking: PublicDetailQuestion[];
    };
  }>(`/v1/detail-questions`);

interface PrimaryCard {
  title: string;
  messages: string[];
  chartData: ChartDatum[];
  actions: CardAction[];
}

interface ChartDatum {
  key: string;
  value: number;
  color: string;
}

interface CardAction {
  iconUrl: string;
  label: string;
  to: string;
}

interface PrimaryTiles {
  title: string;
  weeklyMetrics: WeeklyMetric[];
}

interface WeeklyMetric {
  count: number;
  title: string;
  indicatorCount: number;
  to: string;
  currentUserCount: number;
  ctaText: string;
  ctaTo: string;
}

interface SecondaryTiles {
  title: string;
  suggestedActions: SuggestedAction[];
}

interface SuggestedAction {
  title: string;
  indicatorCount: number;
  to: string;
  imgUrl: string;
}

interface FeaturedFooter {
  title: string;
  sections: {
    title: string;
    imgUrl: string;
    count: number;
    to: string;
  }[];
}

interface EntertainerAppDashboard {
  primaryCard: PrimaryCard;
  primaryTiles: PrimaryTiles;
  secondaryTiles: SecondaryTiles;
  featuredFooter: FeaturedFooter;
}

export const useEntertainerDashboardQuery = ({ cityId }: { cityId: string }) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useEntertainerDashboardQuery.queryKey({ cityId }),
    queryFn: () => useEntertainerDashboardQuery.queryFn({ cityId }),
    enabled: !!cityId,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
  });

  return {
    entertainerDashboardIsReady: isFetched,
    entertainerDashboard: data,
    refetchEntertainerDashboard: refetch,
  };
};

useEntertainerDashboardQuery.queryKey = ({ cityId }: { cityId: string }) => [
  'entertainer-dashboard',
  cityId,
];
useEntertainerDashboardQuery.queryFn = ({ cityId }: { cityId: string }) =>
  cityId
    ? API.get<EntertainerAppDashboard>(`/v1/user/entertainer/dashboard?cityId=${cityId}`)
    : Promise.resolve(undefined);
